import { Pencil, Plus, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Loader } from "@/components/common/loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { deleteDependent } from "@/features/clients/api";
import { DependentFormDialog } from "@/features/clients/components/dependent-form-dialog";
import { useDependents } from "@/features/clients/hooks/use-client-records";
import type { Dependent } from "@/features/clients/types";

export function DependentsSection({ clientId }: { clientId: string }) {
  const { records: dependents, loading, error, refetch } = useDependents(clientId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Dependent | null>(null);
  const [deleting, setDeleting] = useState<Dependent | null>(null);

  function openAdd() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(dependent: Dependent) {
    setEditing(dependent);
    setDialogOpen(true);
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await deleteDependent(clientId, deleting.id);
      toast.success("Dependent removed");
      refetch();
    } catch {
      toast.error("Couldn't remove dependent.");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 flex items-center justify-between border-b px-6 py-4">
        <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
          <Users className="text-primary size-5" /> Dependents
        </h3>
        <Button type="button" size="sm" onClick={openAdd}>
          <Plus className="size-4" /> Add Dependent
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : error ? (
        <p className="text-destructive px-6 py-8 text-sm">{error}</p>
      ) : dependents.length === 0 ? (
        <p className="text-muted-foreground px-6 py-8 text-sm">
          No dependents recorded for this client yet.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Age</TableHead>
              <TableHead>Relationship</TableHead>
              <TableHead>Claimed on Return</TableHead>
              <TableHead>Contributes to Household</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dependents.map((dep) => (
              <TableRow key={dep.id}>
                <TableCell className="font-medium">{dep.name}</TableCell>
                <TableCell>{dep.age ?? "—"}</TableCell>
                <TableCell>{dep.relationship || "—"}</TableCell>
                <TableCell>{dep.claimedOnReturn ? "Yes" : "No"}</TableCell>
                <TableCell>{dep.contributesToHousehold ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${dep.name}`}
                    onClick={() => openEdit(dep)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${dep.name}`}
                    onClick={() => setDeleting(dep)}
                  >
                    <Trash2 className="text-destructive size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <DependentFormDialog
        clientId={clientId}
        dependent={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={refetch}
      />

      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove dependent?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {deleting?.name} from this client's profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
